import { AUTOMATION_ACTION_TYPES } from '../../automation/constants';
import {
  META_ACTION,
  META_IDENTITY_FIELDS,
  MESSAGING_SOURCE,
  isMessagingIdentityComplete,
  isMetaAction,
  isMetaValueSet,
  newMetaData,
} from './metaConversion';

export const RECORD_ACTIONS = {
  create_contact: { source: 'contact', mode: 'create' },
  create_company: { source: 'company', mode: 'create' },
  create_deal: { source: 'deal', mode: 'create' },
  update_contact: { source: 'contact', mode: 'update' },
  update_company: { source: 'company', mode: 'update' },
  update_deal: { source: 'deal', mode: 'update' },
};

export const PIXEL_ACTION = 'trigger_pixel';
export const PIXEL_DESTINATIONS = ['meta', 'google'];
export const PIXEL_EVENT_TYPES = ['purchase', 'lead', 'qualified_lead'];
export const PIXEL_DATA = [
  'name',
  'phone',
  'email',
  'value',
  'product',
  'click_ids',
];

export const WAIT_ACTION = 'wait';
export const WAIT_UNITS = ['seconds', 'minutes', 'hours', 'days'];
const WAIT_UNIT_SECONDS = { seconds: 1, minutes: 60, hours: 3600, days: 86400 };
// Mesmo limite do backend (AutomationFlows::Delay::MAX_SECONDS): 30 dias.
const WAIT_MAX_SECONDS = 30 * 86400;

// Campos calculados pelo sistema, que não podem ser definidos por uma ação.
const READ_ONLY_FIELDS = [
  'created_at',
  'last_activity_at',
  'contacts_count',
  'pipeline_id',
];

export const isRecordAction = key => key in RECORD_ACTIONS;
export const isPixelAction = key => key === PIXEL_ACTION;
export const isWaitAction = key => key === WAIT_ACTION;

export const isConversationAction = key =>
  key !== 'send_webhook_event' &&
  AUTOMATION_ACTION_TYPES.some(action => action.key === key);

export const newFieldRow = () => ({ field: '', value: '' });

export const defaultActionData = () => ({
  action: '',
  value: '',
  fields: [],
  pipeline_id: '',
  stage_id: '',
  destinations: ['meta'],
  event_type: '',
  include: ['name', 'phone', 'email', 'value', 'click_ids'],
  amount: '',
  unit: 'minutes',
  ...newMetaData(),
});

export const initialActionData = (action, label) => ({
  ...defaultActionData(),
  ...(label ? { label } : {}),
  action,
  fields: isRecordAction(action) ? [newFieldRow()] : [],
});

const LABEL_PREFIX = 'AUTOMATION.FLOW.ACTIONS';

export const getActionGroups = ({ dealEvent }) => [
  {
    id: 'conversation',
    labelKey: 'AUTOMATION.FLOW.ACTION_GROUPS.CONVERSATION',
    actions: AUTOMATION_ACTION_TYPES.map(({ key, label }) => ({
      key,
      labelKey: `AUTOMATION.ACTIONS.${label}`,
      disabled: dealEvent && key !== 'send_webhook_event',
    })),
  },
  {
    id: 'records',
    labelKey: 'AUTOMATION.FLOW.ACTION_GROUPS.RECORDS',
    actions: Object.keys(RECORD_ACTIONS).map(key => ({
      key,
      labelKey: `${LABEL_PREFIX}.${key.toUpperCase()}`,
      disabled: false,
    })),
  },
  {
    id: 'integrations',
    labelKey: 'AUTOMATION.FLOW.ACTION_GROUPS.INTEGRATIONS',
    actions: [
      {
        key: META_ACTION,
        labelKey: `${LABEL_PREFIX}.SEND_META_CONVERSION`,
        disabled: false,
      },
      {
        key: PIXEL_ACTION,
        labelKey: `${LABEL_PREFIX}.TRIGGER_PIXEL`,
        disabled: false,
      },
    ],
  },
  {
    id: 'flow',
    labelKey: 'AUTOMATION.FLOW.ACTION_GROUPS.FLOW',
    actions: [
      {
        key: WAIT_ACTION,
        labelKey: `${LABEL_PREFIX}.WAIT`,
        disabled: false,
      },
    ],
  },
];

// Grupos de campos que uma ação de criar/atualizar pode preencher.
export const getRecordFieldGroups = (fieldGroups, action) => {
  const { source, mode } = RECORD_ACTIONS[action] || {};
  const excluded =
    action === 'create_deal'
      ? [...READ_ONLY_FIELDS, 'pipeline_stage_id']
      : READ_ONLY_FIELDS;
  return fieldGroups
    .filter(({ id }) => id === source || id === `${source}_custom`)
    .map(group => ({
      ...group,
      fields: group.fields.filter(
        ({ attribute }) => mode && !excluded.includes(attribute)
      ),
    }));
};

// Alturas dos blocos que o corpo da ação ocupa abaixo do seletor de ação.
export const actionBodyHeights = (data, { field, box }) => {
  if (isWaitAction(data.action)) return [field];
  if (isPixelAction(data.action)) return [field, field, box];
  if (isMetaAction(data.action)) return [field, field, field];
  if (!isRecordAction(data.action)) return [];
  const rows = data.fields || [];
  return [
    ...(data.action === 'create_deal' ? [field, field] : []),
    ...rows.flatMap(() => [field, field]),
    field,
  ];
};

const isBlank = value =>
  value === undefined || value === null || `${value}`.trim() === '';

const validateWait = data => {
  const amount = Number(data.amount);
  if (isBlank(data.amount) || !Number.isInteger(amount) || amount < 1) {
    return ['WAIT_AMOUNT_REQUIRED'];
  }
  return amount * (WAIT_UNIT_SECONDS[data.unit] || 1) > WAIT_MAX_SECONDS
    ? ['WAIT_TOO_LONG']
    : [];
};

export const validateActionBody = data => {
  if (isWaitAction(data.action)) return validateWait(data);
  if (isMetaAction(data.action)) {
    const keys = [];
    if (!data.meta_event) keys.push('META_EVENT_REQUIRED');
    if (data.meta_action_source === MESSAGING_SOURCE) {
      if (!data.meta_messaging_channel) keys.push('META_CHANNEL_REQUIRED');
      else if (!isMessagingIdentityComplete(data)) {
        keys.push('META_MESSAGING_IDENTITY_REQUIRED');
      }
    } else if (
      !META_IDENTITY_FIELDS.some(key => isMetaValueSet(data.meta_fields?.[key]))
    ) {
      keys.push('META_IDENTITY_REQUIRED');
    }
    return keys;
  }
  if (isPixelAction(data.action)) {
    const keys = [];
    if (!data.destinations?.length) keys.push('DESTINATION_REQUIRED');
    if (!data.event_type) keys.push('EVENT_TYPE_REQUIRED');
    return keys;
  }
  const keys = [];
  if (data.action === 'create_deal') {
    if (!data.pipeline_id) keys.push('PIPELINE_REQUIRED');
    if (!data.stage_id) keys.push('STAGE_REQUIRED');
  }
  const rows = data.fields || [];
  if (!rows.length) keys.push('FIELDS_REQUIRED');
  rows.forEach((row, index) => {
    if (!row.field) keys.push(`FIELD_REQUIRED@${index}`);
    if (isBlank(row.value)) keys.push(`FIELD_VALUE_REQUIRED@${index}`);
  });
  return keys;
};
